import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { Alert, AlertTitle, Grid } from '@mui/material';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import { AutogiroSettingsEnum } from 'src/shared/models/enums/autogiroSettings.enum';
import { ControlledAutocompleteDropdown } from 'src/shared/components/form/controlledAutocompleteDropdown';
import { createMemberAutogiroStatus } from 'src/shared/services/memberAutogiroStatus';
import { toast } from 'react-toastify';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { useState } from 'react';
import { addAutogiroMandate } from 'src/shared/services/memberService';
import { AutogiroConnectMemberDataGrid } from './autogiroConnectMemberDataGrid';
import { MemberAutogiroStatusRequest } from '../models/memberAutogiroStatusRequest';
import { Member } from '../models/member';
import { AutogiroMandateConnectMemberRequest } from '../models/autogiroMandateConnectMemberRequest';

type AutogiroSettingsModalProps = {
  openAutogiroSettingsModal: boolean;
  setOpenAutogiroSettingsModal: (openAutogiroSettings: boolean) => void;
  member: Member;
  refetchMemberCallback: () => void;
};

export const AutogiroSettingsModal = (props: AutogiroSettingsModalProps) => {
  const { openAutogiroSettingsModal, setOpenAutogiroSettingsModal, member, refetchMemberCallback } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [openAddAutogiroDialog, setOpenAddAutogiroDialog] = useState<boolean>(false);
  const [autogiroMandateId, setAutogiroMandateId] = useState<AutogiroMandateConnectMemberRequest | undefined>(undefined);
  const [activateNextStep, setActivateNextStep] = useState<boolean>(false);

  const getAutogiroSettingsOptions = [
    { value: AutogiroSettingsEnum.monthly, label: t('member:autogiroSettings.monthly') },
    { value: AutogiroSettingsEnum.annually, label: t('member:autogiroSettings.annually') },
  ];

  const requiredMessage = t('common:validationMessages.required');
  const typeErrorMessage = t('common:validationMessages.invalidFormat');
  const autogiroSettingsSchema: yup.Schema<MemberAutogiroStatusRequest> = yup.object<MemberAutogiroStatusRequest>().shape({
    owner_id: yup.number().required(),
    customer_id: yup.number().required(),
    periodicity: yup.number().required(requiredMessage).typeError(typeErrorMessage),
  });

  const formMethods = useForm<MemberAutogiroStatusRequest>({
    resolver: yupResolver(autogiroSettingsSchema),
    mode: 'all',
    defaultValues: {
      owner_id: undefined,
      customer_id: member.member_id,
      periodicity: undefined,
    },
  });

  const { handleSubmit, setValue, reset, watch } = formMethods;

  const onSubmit = async (data: MemberAutogiroStatusRequest) => {
    try {
      await createMemberAutogiroStatus(data);
      if (isMounted()) {
        toast.success(<>{t('member:autogiroSettings.updatedAutogiro')}</>);
        setOpenAutogiroSettingsModal(false);
        refetchMemberCallback();
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    }
  };

  const onHandleClose = () => {
    reset();
    setOpenAutogiroSettingsModal(false);
    if (activateNextStep) {
      refetchMemberCallback();
    }
  };

  const handleAddAutogiroMandate = async (autogiroMandate: { id: string }) => {
    if (member.member_id && autogiroMandate) {
      try {
        const data = { autogiroMandateId: Number(autogiroMandate.id) } as AutogiroMandateConnectMemberRequest;
        await addAutogiroMandate(member.member_id, data);
        if (isMounted()) {
          toast.success(<>{t('member:autogiroSettings.mandateConnected')}</>);
          setActivateNextStep(true);
          setOpenAddAutogiroDialog(false);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      }
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form>
        <GenericDialog
          dialogTitle={t('member:addAutogiro')}
          onSave={handleSubmit(onSubmit)}
          onClose={onHandleClose}
          open={openAutogiroSettingsModal}
          maxWidth='md'
          showDialogActions={!!member.autogiroMandate || activateNextStep}
          dialogContent={
            <GridWrapper>
              {(member.autogiroMandate || activateNextStep) && (
                <>
                  <Grid item>
                    <ControlledAutocompleteDropdown
                      nameProp='label'
                      name='periodicity'
                      label={t('member:autogiroSettings.periodicity')}
                      options={getAutogiroSettingsOptions}
                      callback={(selectedOption) => {
                        if (selectedOption) {
                          setValue('periodicity', selectedOption.value);
                        } else {
                          setValue('periodicity', undefined);
                        }
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <InvoiceGroups
                      required
                      ownerId={watch('owner_id')}
                      emptyLabel={t('common:button.choose')}
                      placeholder={t('common:button.choose')}
                      callback={(ownId) => {
                        if (ownId === 0) {
                          setValue('owner_id', undefined);
                        } else {
                          setValue('owner_id', ownId);
                        }
                      }}
                    />
                  </Grid>
                </>
              )}
              {!member.autogiroMandate && !activateNextStep && (
                <>
                  <Grid item xs={12}>
                    <Alert sx={{ whiteSpace: 'pre-line' }} aria-label='info' severity='warning'>
                      <AlertTitle>{t('member:autogiroSettings.alertTitle')}</AlertTitle>
                      {t('member:autogiroSettings.alertText')}
                    </Alert>
                  </Grid>
                  <Grid item xs={12}>
                    <AutogiroConnectMemberDataGrid setOpenAddAutogiroDialog={setOpenAddAutogiroDialog} setAutogiroMandateId={setAutogiroMandateId} />
                    {openAddAutogiroDialog && autogiroMandateId && (
                      <DeleteConfirmationDialog
                        title={t('member:autogiroSettings.confirmMandateConnection')}
                        value={autogiroMandateId.toString()}
                        open={openAddAutogiroDialog}
                        onClose={(autogiroMandate) => {
                          if (autogiroMandate) {
                            handleAddAutogiroMandate(autogiroMandate);
                          } else {
                            setAutogiroMandateId(undefined);
                            setOpenAddAutogiroDialog(false);
                          }
                        }}
                      />
                    )}
                  </Grid>
                </>
              )}
            </GridWrapper>
          }
        />
      </form>
    </FormProvider>
  );
};
